import * as Location from 'expo-location';
import i18n from '@/utils/i18n';
import MapView from 'react-native-maps';
import NavigationIcon from '@assets/icons/navigation.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AddressSearchResult,
  useGoogleApiAddressSearchLoader,
} from '@/experiences/sales-portal/components/address-search-input/use-google-api-address-search-loader';
import { BaseMapView } from '@usepepper/ui/molecules/map/base-map-view';
import { BusinessPinCard } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/business-pin-card';
import { Button, Typography } from '@usepepper/sprout-kit-ui';
import { captureException } from '@/utils/sentry';
import { ChatStatus } from '@/types';
import { Coordinate, Region } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import { FilterChipRow } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/filter-chip-row';
import { GooglePlaces } from '@domain/addresses/blocs/services/google-places';
import { LocationSearchInput } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/location-search-input';
import { MapMarker } from '@/experiences/sales-portal/components/map/map-marker';
import {
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import {
  NearbyBusiness,
  NearbyBusinessFilters,
  useProspectFinderNearbyBusinessesLoader,
} from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { PinCardOverlay } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/pin-card-overlay';
import { ProspectsListSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/prospects-list-skeleton';
import { ProspectsResultsList } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/prospects-results-list';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalAnalyticEvent } from '@/experiences/sales-portal/analytics/fennel/registry';
import { SalesPortalStackParams } from '@/navigators/types';
import { SearchThisAreaButton } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/search-this-area-button';
import { StackNavigationProp } from '@react-navigation/stack';
import { track } from '@/experiences/sales-portal/analytics/fennel/hook';
import { useCreateProspectAccountMutator } from '@/experiences/sales-portal/domain/prospects/blocs/mutators/use-create-prospect-account-mutator';
import { useDirectHitRetry } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/blocs/hooks/use-direct-hit-retry';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useNavigation } from '@react-navigation/native';
import { useProspectFinderFiltering } from '@/experiences/sales-portal/domain/prospects/blocs/hooks/use-prospect-finder-filtering';
import { useProspectFinderMapInteractions } from '@/experiences/sales-portal/domain/prospects/blocs/hooks/use-prospect-finder-map-interactions';
import { useProspectFinderSupplierHqLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/blocs/loaders/use-prospect-finder-supplier-hq-loader';
import { UserLocationDot } from '@/experiences/sales-portal/components/map/user-location-dot';
import { View } from 'react-native';

const REGION_DELTA = 0.005;
// Wider zoom for the HQ anchor; rep can pan/zoom in once they orient.
const HQ_REGION_DELTA = 0.02;

const regionToBoundsLiteral = (
  region: Region,
): google.maps.LatLngBoundsLiteral => ({
  east: region.longitude + region.longitudeDelta / 2,
  north: region.latitude + region.latitudeDelta / 2,
  south: region.latitude - region.latitudeDelta / 2,
  west: region.longitude - region.longitudeDelta / 2,
});
const LEFT_COLUMN_WIDTH = 380;
const BANNER_MAX_WIDTH = 520;

const PROSPECTS_V2_PRODUCT_ID = 'prospects-v2';

const getDetailRoute = (isProspectsV2Enabled: boolean, status?: ChatStatus) =>
  isProspectsV2Enabled && status === ChatStatus.Prospect
    ? SALES_PORTAL_ROUTES.ProspectDetail
    : SALES_PORTAL_ROUTES.AccountDetail;

// Web's Places service returns a google.maps.LatLng (lat()/lng() methods);
// the native Places REST response returns plain numbers. Read either.
const getPlaceCoordinate = (
  location: google.maps.LatLng | google.maps.LatLngLiteral | undefined,
): Coordinate | null => {
  if (!location) {
    return null;
  }
  const lat =
    typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng =
    typeof location.lng === 'function' ? location.lng() : location.lng;
  return typeof lat === 'number' && typeof lng === 'number'
    ? { latitude: lat, longitude: lng }
    : null;
};

type PermissionState = 'unknown' | 'granted' | 'prompt' | 'denied';

interface Props {
  supplierUUID: string;
}

export const ProspectFinderContent: React.FC<Props> = ({ supplierUUID }) => {
  const [styles, theme] = useStyles();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();

  const filtering = useProspectFinderFiltering();
  const interactions = useProspectFinderMapInteractions();

  const [permissionState, setPermissionState] =
    useState<PermissionState>('unknown');
  // Mirrors mobile's two-state pattern: last-known returns fast (possibly
  // stale) so the map paints quickly; current overrides once it resolves.
  const [lastKnownCoords, setLastKnownCoords] = useState<Coordinate | null>(
    null,
  );
  const [currentCoords, setCurrentCoords] = useState<Coordinate | null>(null);
  const [currentCoordsFailed, setCurrentCoordsFailed] = useState(false);
  const [primingDismissed, setPrimingDismissed] = useState(false);
  // True only when rep clicked the priming Enable button.
  // Permission state flipping for any other reason
  // (e.g. browser-settings re-enable mid-session) does NOT animate the map.
  const [userRequestedRecenter, setUserRequestedRecenter] = useState(false);

  // HQ anchors the pre-grant state OR a granted-but-failed geolocation
  // (e.g. browser rejects the position request); without the fallback the
  // skeleton would hang forever.
  const userCenter = currentCoords ?? lastKnownCoords;
  const needsHqAnchor =
    permissionState === 'prompt' ||
    permissionState === 'denied' ||
    (permissionState === 'granted' && currentCoordsFailed && !userCenter);
  const { coordinates: hqCoords } = useProspectFinderSupplierHqLoader({
    enabled: needsHqAnchor,
    supplierUUID,
  });

  // Read permission without triggering a prompt.
  useEffect(() => {
    let cancelled = false;
    Location.getForegroundPermissionsAsync()
      .then(({ status }) => {
        if (cancelled) {
          return;
        }
        setPermissionState(
          status === Location.PermissionStatus.GRANTED
            ? 'granted'
            : status === Location.PermissionStatus.DENIED
              ? 'denied'
              : 'prompt',
        );
      })
      .catch(() => {
        // Older browsers without the Permissions API throw here; fall through
        // to the priming UI so the rep can still trigger the system prompt.
        if (!cancelled) {
          setPermissionState('prompt');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch last-known (fast, possibly stale) and current (accurate) in
  // parallel, ensures map isn't blank while accurate GPS resolves
  useEffect(() => {
    if (permissionState !== 'granted') {
      return;
    }
    setCurrentCoordsFailed(false);
    let cancelled = false;
    void Location.getLastKnownPositionAsync().then((position) => {
      if (cancelled || !position) {
        return;
      }
      setLastKnownCoords({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    });
    void Location.getCurrentPositionAsync({}).then(
      (position) => {
        if (cancelled) {
          return;
        }
        setCurrentCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      // Safari can grant permission but still reject the position request — without this flag the skeleton hangs forever.
      () => {
        if (!cancelled) {
          setCurrentCoordsFailed(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [permissionState]);

  const handlePrimingAccept = async () => {
    track(SalesPortalAnalyticEvent.PROSPECT_FINDER_ENABLE_LOCATION_CLICK, {});
    let status: Location.PermissionStatus;
    try {
      ({ status } = await Location.requestForegroundPermissionsAsync());
    } catch {
      // Treat unexpected rejection as denied so the rep still sees the banner
      setPermissionState('denied');
      setPrimingDismissed(true);
      return;
    }
    if (status === Location.PermissionStatus.GRANTED) {
      setPermissionState('granted');
      setUserRequestedRecenter(true);
      setPrimingDismissed(true);
      // Snapping to the rep's location drops any viewport override.
      interactions.onRecenter();
    } else if (status === Location.PermissionStatus.DENIED) {
      setPermissionState('denied');
      setPrimingDismissed(true);
    }
    // Any other status (e.g. undetermined): leave the banner so the rep
    // can retry without reloading.
  };

  const searchCenter = userCenter ?? (needsHqAnchor ? hqCoords : null);
  const isHqAnchor = !userCenter && !!searchCenter;
  const delta = isHqAnchor ? HQ_REGION_DELTA : REGION_DELTA;

  // Freeze the first valid center so later searchCenter churn doesn't re-mount the map
  const [initialRegion, setInitialRegion] = useState<Region | null>(null);
  useEffect(() => {
    if (initialRegion || !searchCenter) {
      return;
    }
    setInitialRegion({
      latitude: searchCenter.latitude,
      latitudeDelta: delta,
      longitude: searchCenter.longitude,
      longitudeDelta: delta,
    });
  }, [initialRegion, searchCenter, delta]);

  const mapRef = useRef<MapView | null>(null);
  // Bumped on each map settle so the pin-card overlay re-projects.
  const [projectionTick, setProjectionTick] = useState(0);

  // Our recenter pan can intermittently fire onPanDrag; ignore it while one is
  // in flight so it doesn't dismiss the card we just opened.
  const programmaticPanRef = useRef(false);
  const lastPanTargetRef = useRef(interactions.panToCoordinate);
  if (lastPanTargetRef.current !== interactions.panToCoordinate) {
    lastPanTargetRef.current = interactions.panToCoordinate;
    programmaticPanRef.current = !!interactions.panToCoordinate;
  }

  // Entry-point analytics: fire once when the map first has a region to render.
  const mapTrackedRef = useRef(false);
  useEffect(() => {
    if (initialRegion && !mapTrackedRef.current) {
      mapTrackedRef.current = true;
      track(SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_CLICK, {});
    }
  }, [initialRegion]);

  // Only animate when the rep explicitly asked us to find them.
  const recenterRegion = useMemo<Region | undefined>(() => {
    if (!userRequestedRecenter || !userCenter) {
      return undefined;
    }
    return {
      latitude: userCenter.latitude,
      latitudeDelta: REGION_DELTA,
      longitude: userCenter.longitude,
      longitudeDelta: REGION_DELTA,
    };
  }, [userRequestedRecenter, userCenter]);

  // Clear on terminal state so a later re-grant doesn't re-animate.
  useEffect(() => {
    if (userRequestedRecenter && (userCenter || currentCoordsFailed)) {
      setUserRequestedRecenter(false);
    }
  }, [userRequestedRecenter, userCenter, currentCoordsFailed]);

  // "Search this area" overrides the query location until recenter.
  const queryCenter = interactions.searchAreaLocation ?? searchCenter;

  const nearbyFilters: NearbyBusinessFilters = useMemo(
    () => ({
      categories: filtering.categories,
      isCurrentRestaurant: filtering.isCurrentRestaurant,
      latitude: queryCenter?.latitude ?? 0,
      longitude: queryCenter?.longitude ?? 0,
      price: filtering.price,
      radius: interactions.searchAreaLocation?.radius ?? null,
      sortBy: filtering.sortOrder,
    }),
    [
      filtering.categories,
      filtering.isCurrentRestaurant,
      filtering.price,
      filtering.sortOrder,
      interactions.searchAreaLocation?.radius,
      queryCenter?.latitude,
      queryCenter?.longitude,
    ],
  );

  const {
    error: nearbyError,
    fetchMore: fetchMoreNearby,
    fetchingMore: fetchingMoreNearby,
    loading: nearbyLoading,
    nearbyBusinesses,
    retry: retryNearby,
    supplierCountryCode,
  } = useProspectFinderNearbyBusinessesLoader(supplierUUID, nearbyFilters, {
    enabled: !!searchCenter,
  });

  // Fit the map to the next non-empty result set
  const [didInitialFit, setDidInitialFit] = useState(false);
  const initialFitRegion = useMemo<Region | undefined>(() => {
    if (didInitialFit || !nearbyBusinesses?.length) {
      return undefined;
    }
    const lats = nearbyBusinesses.map((b) => b.latitude);
    const lngs = nearbyBusinesses.map((b) => b.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return {
      latitude: (minLat + maxLat) / 2,
      // Single-pin or degenerate bounds fall back to the default zoom.
      latitudeDelta: maxLat - minLat || REGION_DELTA,
      longitude: (minLng + maxLng) / 2,
      longitudeDelta: maxLng - minLng || REGION_DELTA,
    };
  }, [didInitialFit, nearbyBusinesses]);
  useEffect(() => {
    if (initialFitRegion) {
      setDidInitialFit(true);
    }
  }, [initialFitRegion]);

  const { onFilterChange } = interactions;
  const handleFilterChange = useCallback(() => {
    setDidInitialFit(false);
    onFilterChange();
  }, [onFilterChange]);

  // Mirror the cache. Keep the stale card while reloading; drop it if
  // the row's gone.
  const selectedBusiness = useMemo(() => {
    const selected = interactions.selectedBusiness;
    if (!selected) {
      return undefined;
    }
    if (!nearbyBusinesses) {
      return selected;
    }
    return nearbyBusinesses.find(
      (business) => business.yelpBusinessId === selected.yelpBusinessId,
    );
  }, [interactions.selectedBusiness, nearbyBusinesses]);

  const { claimingYelpIds, createAccount, listClaimingYelpIds } =
    useCreateProspectAccountMutator(supplierUUID, nearbyFilters);

  const { slots: microProductSlots } = useMicroProductsLoader();
  const isProspectsV2Enabled = useMemo(
    () =>
      microProductSlots
        .find((s) => s.slot === MicroProductsSlot.SALES_HUB_SIDEBAR)
        ?.products.some((p) => p.id === PROSPECTS_V2_PRODUCT_ID) ?? false,
    [microProductSlots],
  );

  const handleViewDetails = ({
    restaurantUuid,
    status,
    supplierUuid,
    yelpBusinessId,
  }: NearbyBusiness) => {
    if (!restaurantUuid || !supplierUuid) {
      return;
    }
    track(SalesPortalAnalyticEvent.PROSPECT_FINDER_VIEW_ACCOUNT_DETAILS_CLICK, {
      restaurantUUID: restaurantUuid,
      yelpBusinessId,
    });
    navigateToSalesPortalRoute(
      navigation,
      getDetailRoute(isProspectsV2Enabled, status),
      {
        restaurantUUID: restaurantUuid,
        supplierUUID: supplierUuid,
      },
    );
  };

  const { findDirectHit } = useDirectHitRetry(supplierUUID, nearbyFilters);
  const { showErrorToast } = useExperienceToasts();

  // Fly the map as soon as we have coords, then run the direct-hit retry in
  // the background; if it matches a business, open the card.
  const { onBusinessSelect, onRecenter, selectSearchLocation } = interactions;
  // Each invocation bumps the token; only the latest pick may open a card,
  // so a slow probe for an earlier pick can't pop a stale pin.
  const selectPlaceTokenRef = useRef(0);
  const handleSelectPlace = useCallback(
    async (result: AddressSearchResult) => {
      selectPlaceTokenRef.current += 1;
      const token = selectPlaceTokenRef.current;
      try {
        const details = await GooglePlaces.getPlaceDetails(result.placeId);
        const location = getPlaceCoordinate(details.geometry?.location);
        if (!location) {
          return;
        }
        if (selectPlaceTokenRef.current !== token) {
          return;
        }
        // refit the map bounds so all result pins fit within it
        setDidInitialFit(false);
        selectSearchLocation(location);

        const isBusiness = (details.types ?? []).includes('establishment');
        track(SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_SEARCH, {
          searchType: isBusiness ? 'establishment' : 'geocode',
        });
        if (isBusiness) {
          const hit = await findDirectHit(location, result.label);
          if (hit && selectPlaceTokenRef.current === token) {
            onBusinessSelect(hit);
          }
        }
      } catch (error) {
        captureException(error);
        showErrorToast(
          i18n.t('sales_portal.prospect_finder.location_search.error'),
        );
      }
    },
    [findDirectHit, onBusinessSelect, selectSearchLocation, showErrorToast],
  );

  const handleCurrentLocation = useCallback(() => {
    track(SalesPortalAnalyticEvent.PROSPECT_FINDER_RECENTER_CLICK, {});
    onRecenter();
    setUserRequestedRecenter(true);
  }, [onRecenter]);

  // Hold the skeleton until coords resolve so we never flash an empty state.
  const isInitialLoad = !searchCenter || (nearbyLoading && !nearbyBusinesses);

  // Bias autocomplete toward the current viewport. Read live from the idle
  // ref (no re-render on pan), so it's current as of the last render.
  const idleRegion = interactions.lastIdleRegion.current ?? initialRegion;
  const searchLocationBias = idleRegion
    ? regionToBoundsLiteral(idleRegion)
    : undefined;

  // Business name, address, city, zip, or neighborhood (per PRD).
  const {
    addressResults,
    clear,
    hasError,
    isSearching,
    lastSearched,
    retry,
    search,
  } = useGoogleApiAddressSearchLoader({
    locationBias: searchLocationBias,
    types: ['establishment', 'geocode'],
  });

  const showPriming = permissionState === 'prompt' && !primingDismissed;
  const showDeniedBanner = permissionState === 'denied';

  return (
    <View style={styles.container}>
      <View style={styles.leftColumn}>
        <LocationSearchInput
          addressResults={addressResults}
          hasError={hasError}
          isSearching={isSearching}
          lastSearched={lastSearched}
          onClear={clear}
          onRetry={retry}
          onSearch={search}
          onSelectPlace={handleSelectPlace}
        />
        <FilterChipRow
          filtering={filtering}
          onFilterChange={handleFilterChange}
        />
        <View style={styles.listContainer}>
          {isInitialLoad ? (
            <ProspectsListSkeleton />
          ) : (
            <ProspectsResultsList
              claimingYelpIds={listClaimingYelpIds}
              error={nearbyError}
              fetchingMore={fetchingMoreNearby}
              fetchMore={fetchMoreNearby}
              nearbyBusinesses={nearbyBusinesses}
              onClaim={(business) => createAccount(business, 'list')}
              onRowPress={(business) => {
                track(
                  SalesPortalAnalyticEvent.PROSPECT_FINDER_RESULT_ROW_CLICK,
                  {
                    isExisting: !!business.restaurantUuid,
                    yelpBusinessId: business.yelpBusinessId,
                  },
                );
                interactions.onBusinessSelect(business);
              }}
              retry={() => {
                track(SalesPortalAnalyticEvent.PROSPECT_FINDER_RETRY_CLICK, {});
                retryNearby();
              }}
              selectedYelpId={selectedBusiness?.yelpBusinessId}
              supplierCountryCode={supplierCountryCode}
            />
          )}
        </View>
      </View>
      <View style={styles.mapColumn}>
        {initialRegion ? (
          <BaseMapView
            animateToRegion={recenterRegion ?? initialFitRegion}
            clickableIcons={false}
            initialRegion={initialRegion}
            interactive
            mapRef={mapRef}
            onCurrentLocation={handleCurrentLocation}
            // A user drag dismisses the open card; ignore our own recenter pan.
            onPanDrag={() => {
              if (programmaticPanRef.current) {
                return;
              }
              interactions.onPanDrag();
              interactions.onMapPress();
            }}
            onPress={interactions.onMapPress}
            onRegionChangeComplete={(region) => {
              interactions.onRegionChangeComplete(region);
              programmaticPanRef.current = false;
              setProjectionTick((tick) => tick + 1);
            }}
            onZoomChange={interactions.onZoomChange}
            panToCoordinate={interactions.panToCoordinate}
            showZoomControl
          >
            {userCenter ? <UserLocationDot coordinate={userCenter} /> : null}
            {nearbyBusinesses?.map((business) => (
              <MapMarker
                accessibilityLabel={business.businessName}
                coordinate={{
                  latitude: business.latitude,
                  longitude: business.longitude,
                }}
                fill={
                  business.restaurantUuid
                    ? theme.sk.color.bg.toast
                    : theme.sk.color.icon.positive.primary
                }
                key={business.yelpBusinessId}
                onPress={() => {
                  track(
                    SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_PIN_CLICK,
                    {
                      isExisting: !!business.restaurantUuid,
                      yelpBusinessId: business.yelpBusinessId,
                    },
                  );
                  interactions.onBusinessSelect(business);
                }}
                selected={
                  business.yelpBusinessId === selectedBusiness?.yelpBusinessId
                }
              />
            )) ?? null}
            {selectedBusiness ? (
              <BusinessPinCard
                business={selectedBusiness}
                claiming={claimingYelpIds.has(selectedBusiness.yelpBusinessId)}
                onClaim={(business) => createAccount(business, 'card')}
                onClose={() => {
                  track(
                    SalesPortalAnalyticEvent.PROSPECT_FINDER_PIN_CARD_CLOSE_CLICK,
                    { yelpBusinessId: selectedBusiness.yelpBusinessId },
                  );
                  interactions.onMapPress();
                }}
                onViewDetails={handleViewDetails}
                supplierCountryCode={supplierCountryCode}
              />
            ) : null}
          </BaseMapView>
        ) : null}
        {selectedBusiness ? (
          <PinCardOverlay
            business={selectedBusiness}
            claiming={claimingYelpIds.has(selectedBusiness.yelpBusinessId)}
            mapRef={mapRef}
            onClaim={(business) => createAccount(business, 'card')}
            onClose={() => {
              track(
                SalesPortalAnalyticEvent.PROSPECT_FINDER_PIN_CARD_CLOSE_CLICK,
                { yelpBusinessId: selectedBusiness.yelpBusinessId },
              );
              interactions.onMapPress();
            }}
            onViewDetails={handleViewDetails}
            panPending={!!interactions.panToCoordinate}
            projectionTick={projectionTick}
            supplierCountryCode={supplierCountryCode}
          />
        ) : null}
        {showPriming || showDeniedBanner || interactions.mapChanged ? (
          // Banner (if any) stacks above the search-area pill so the two never
          // overlap — e.g. "search this area" sits below the blocked banner.
          <View pointerEvents="box-none" style={styles.topOverlay}>
            {showPriming ? (
              <View style={styles.primingBanner}>
                <View style={styles.primingIconChip}>
                  <NavigationIcon height={20} width={20} />
                </View>
                <View style={styles.primingCopy}>
                  <Typography bold size="$sm">
                    {i18n.t(
                      'sales_portal.prospect_finder.location_priming.title',
                    )}
                  </Typography>
                  <Typography muted size="$xs">
                    {i18n.t(
                      'sales_portal.prospect_finder.location_priming.body',
                    )}
                  </Typography>
                </View>
                <Button
                  alignSelf="center"
                  onPress={handlePrimingAccept}
                  size="$sm"
                  variant="primary"
                >
                  {i18n.t('sales_portal.prospect_finder.location_priming.cta')}
                </Button>
              </View>
            ) : null}
            {showDeniedBanner ? (
              <View style={styles.deniedBanner}>
                <Typography bold>
                  {i18n.t('sales_portal.prospect_finder.location_denied.title')}
                </Typography>
                <Typography muted size="$sm">
                  {i18n.t('sales_portal.prospect_finder.location_denied.body')}
                </Typography>
              </View>
            ) : null}
            {interactions.mapChanged ? (
              <SearchThisAreaButton
                onPress={() => {
                  track(
                    SalesPortalAnalyticEvent.PROSPECT_FINDER_SEARCH_THIS_AREA_CLICK,
                    {},
                  );
                  interactions.onSearchArea();
                }}
              />
            ) : null}
          </View>
        ) : null}
        {initialRegion ? (
          <View style={styles.legend}>
            <View style={styles.legendRow}>
              <View
                style={[styles.legendSwatch, styles.legendSwatchUnclaimed]}
              />
              <Typography size="$xs">
                {i18n.t(
                  'sales_portal.prospect_finder.legend.unclaimed_prospect',
                )}
              </Typography>
            </View>
            <View style={styles.legendRow}>
              <View style={[styles.legendSwatch, styles.legendSwatchClaimed]} />
              <Typography size="$xs">
                {i18n.t('sales_portal.prospect_finder.legend.existing')}
              </Typography>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  deniedBanner: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    gap: theme.sk.spacing.spaceXsmall,
    maxWidth: BANNER_MAX_WIDTH,
    padding: theme.sk.spacing.spaceMedium,
    width: '100%',
  },
  leftColumn: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRightColor: theme.sk.color.border.secondary,
    borderRightWidth: 1,
    flexDirection: 'column',
    width: LEFT_COLUMN_WIDTH,
  },
  legend: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    bottom: theme.sk.spacing.spaceMedium,
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceMedium,
    position: 'absolute',
    right: theme.sk.spacing.spaceMedium,
  },
  legendRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  legendSwatch: {
    borderRadius: 6,
    height: 12,
    width: 12,
  },
  legendSwatchClaimed: {
    backgroundColor: theme.sk.color.bg.toast,
  },
  legendSwatchUnclaimed: {
    backgroundColor: theme.sk.color.icon.positive.primary,
  },
  listContainer: {
    flex: 1,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  mapColumn: {
    flex: 1,
  },
  primingBanner: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    boxShadow:
      '0 8px 28px rgba(15, 23, 42, 0.16), 0 1px 3px rgba(15, 23, 42, 0.08)',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    maxWidth: BANNER_MAX_WIDTH,
    padding: theme.sk.spacing.spaceSMedium,
    width: '100%',
  },
  primingCopy: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  primingIconChip: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusRound,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  topOverlay: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceSmall,
    left: 0,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    position: 'absolute',
    right: 0,
    top: theme.sk.spacing.spaceMedium,
  },
}));
